import { Store } from "redux";
import { Map } from "immutable";

import { GlobalState } from "@app/models/state";
import { getInitialState as authState } from "@auth/reducers";
import { User, Organisation, Role, Permission } from "@auth/models";
import { getInitialState as localisationState } from "@localisation/reducers";
import { LocalisationInternalState } from "@localisation/models";

export const subscribe = (store: Store<GlobalState>) => {
    store.subscribe(() => {
        const { auth, localisation, sales } = store.getState();

        // Don't persist all of auth.
        const authToSave: any = authState();
        authToSave.accessToken = auth.accessToken;
        authToSave.authenticated = auth.authenticated;
        authToSave.user = auth.user;

        const localisationToSave: any = {
            language: localisation.language,
        };

        // We need to serialise the entities.
        authToSave.entities.users = auth.entities.users.toObject();
        authToSave.entities.organisations = auth.entities.organisations.toObject();
        authToSave.entities.roles = auth.entities.roles.toObject();
        authToSave.entities.permissions = auth.entities.permissions.toObject();

        // Persist the sales settings.
        const salesToSave: any = {
            settings: {
                from: sales.settings.from.valueOf(),
                to: sales.settings.to.valueOf(),
                type: sales.settings.type,
                countries: sales.settings.countries,
            },
        };

        // Persist auth and localisation
        localStorage.setItem("auth", JSON.stringify(authToSave));
        localStorage.setItem("localisation", JSON.stringify(localisationToSave));
        localStorage.setItem("sales", JSON.stringify(salesToSave));
    });
};

export const getInitialState = (): GlobalState => {
    const state: GlobalState = {
        auth: null,
        reports: null,
        localisation: null,
        api: null,
        sales: null,
        charts: null,
        deliveries: null,
        dsps: null,
        music: null,
        oldwata: null,
        files: null,
        search: null,
        upload: null,
        vendor: null,
    };

    // Get the auth state in.
    const authStateStr = localStorage.getItem("auth");
    if (authStateStr != null) {
        try {
            const authStateObj = JSON.parse(authStateStr);
            state.auth = {
                accessToken: authStateObj.accessToken,
                authenticated: authStateObj.authenticated,
                user: authStateObj.user,
                requests: authStateObj.requests,
                entities: {
                    organisations: Map<number, Organisation>(
                        Object.keys(authStateObj.entities.organisations).map(
                            (key: string) => {
                                const data = authStateObj.entities.organisations[key];
                                data.createdAt = new Date(data.createdAt);
                                data.updatedAt = new Date(data.updatedAt);
                                return [Number(key), new Organisation(data)];
                            },
                        ),
                    ),
                    permissions: Map<number, Permission>(
                        Object.keys(authStateObj.entities.permissions).map(
                            (key: string) =>
                                [Number(key), new Permission(authStateObj.entities.permissions[key])],
                        ),
                    ),
                    roles: Map<string, Role>(
                        Object.keys(authStateObj.entities.roles).map(
                            (key: string) => {
                                const data = authStateObj.entities.roles[key];
                                data.createdAt = new Date(data.createdAt);
                                data.updatedAt = new Date(data.updatedAt);
                                return [key, new Role(data)];
                            },
                        ),
                    ),
                    users: Map<number, User>(
                        Object.keys(authStateObj.entities.users).map(
                            (key: string) => {
                                const data = authStateObj.entities.users[key];
                                data.createdAt = new Date(data.createdAt);
                                data.updatedAt = new Date(data.updatedAt);
                                return [Number(key), new User(data)];
                            },
                        ),
                    ),
                },
            };
        }
        catch (e) { /* do nothing */ }
    }

    // Get the localisation state in.
    const localisationStateStr = localStorage.getItem("localisation");
    if (localisationStateStr != null) {
        try {
            const storeLocalisation = JSON.parse(localisationStateStr) as LocalisationInternalState;
            state.localisation = localisationState(storeLocalisation.language);
        } catch (e) { /* do nothing */ }
    }

    // Get the sales state in.
    // const salesStateStr = localStorage.getItem("sales");
    // if (salesStateStr != null) {
    //     try {
    //         const salesSettings = JSON.parse(salesStateStr);
    //         state.sales = salesState();
    //         state.sales.settings.from = moment(Number(salesSettings.settings.from));
    //         state.sales.settings.to = moment(Number(salesSettings.settings.to));
    //         state.sales.settings.type = salesSettings.settings.type;
    //         state.sales.settings.countries = salesSettings.settings.countries;
    //     }
    //     catch (e) { /* do nothing */ }
    // }

    return state;
};
